import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale } from 'next-intl';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import Separator from '../../../../components/simple/Separator';
import SocialLogin from '../../components/SocialLogin';

import PromoCodeAlert from '@components/Checkout/PromoCodeAlert';
import { Button, Input, Checkbox } from '@components/Form';
import { Link } from '@lib/navigation';

const schema = z.object({
  email: z.string().email(),
  marketingAgreement: z.boolean().refine((value) => value === true),
  commercialInside: z.boolean().optional(),
  commercialOutside: z.boolean().optional(),
});

const schemaDE = z.object({
  email: z.string().email(),
  agreementDE: z.boolean().refine((value) => value === true),
});

export const isDESchema = (
  _schema: z.infer<typeof schema> | z.infer<typeof schemaDE>,
): _schema is z.infer<typeof schemaDE> => {
  return 'agreementDE' in _schema;
};

interface RegisterWithEmailFormProps {
  className?: string;
  onSuccess?: (email: string) => void;
  onSubmit: (data: any, setError: (...data: any) => void) => Promise<void>;
}
export default function RegisterWithEmailForm({
  className,
  onSubmit
}: RegisterWithEmailFormProps) {
  const locale = useLocale();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof schema> | z.infer<typeof schemaDE>>({
    resolver: zodResolver(locale !== 'de' ? schema : schemaDE),
  });

  const t = useTranslations('auth');

  const onSubmitForm = handleSubmit(async (data) => {
    await onSubmit(data, setError);
  });

  return (
    <form className={className} onSubmit={onSubmitForm}>
      <h1 className="mb-3 text-xl font-bold text-center text-premium-900 lg:text-2xl dark:text-white">
        {t('register.title')}
      </h1>
      <h2 className="text-md mb-3 font-bold text-center text-premium-900 lg:mb-4 lg:text-lg dark:text-white">
        {t('register.registerWithEmail')}
      </h2>

      <div className="mb-2 lg:mb-3">
        <Input
          className="mb-1"
          type="email"
          placeholder={t('register.email')}
          error={!!errors.email}
          {...register('email')}
        />
        {errors.email && (
          <p className="text-xs text-red-500">{errors.email.message}</p>
        )}
      </div>

      {locale !== 'de' ? (
        <>
          <Checkbox
            required
            label={t('register.agreement.marketing.label')}
            tooltip={t('register.agreement.marketing.contents')}
            wrapperClassName="mb-2 lg:mb-3"
            // @ts-ignore
            error={!!errors.marketingAgreement}
            {...register('marketingAgreement')}
          />
          <Checkbox
            label={t('register.agreement.commercialInside.label')}
            tooltip={t('register.agreement.commercialInside.contents')}
            wrapperClassName="mb-2 lg:mb-3"
            {...register('commercialInside')}
          />
          <Checkbox
            label={t('register.agreement.commercialOutside.label')}
            tooltip={t('register.agreement.commercialOutside.contents')}
            wrapperClassName="mb-2 lg:mb-3"
            {...register('commercialOutside')}
          />
        </>
      ) : (
        <Checkbox
          label={
            <span>
              Ich habe die&nbsp;
              <Link className="text-premium-500 underline" href="/rules">
                Nutzungsbedingungen
              </Link>
              &nbsp;gelesen und zur Kenntnis genommen
            </span>
          }
          wrapperClassName="mb-2 lg:mb-3"
          {...register('agreementDE')}
        />
      )}
      <PromoCodeAlert suggestPromoCode={false} className="mb-2" />

      <div className="flex flex-col justify-center items-center">
        <Button
          className="mb-4 lg:mb-8"
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {t('register.submit')}
        </Button>
      </div>

      <Separator className="mb-4 lg:mb-8">{t('label.or')}</Separator>

      <SocialLogin
        className="mb-2 lg:mb-4"
        provider="transid"
        label={t('socialLogin.transID')}
      />
      <SocialLogin provider="linkedin" label={t('socialLogin.linkedIn')} />
    </form>
  );
}
